import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Cake, PartyPopper, X, Gift } from 'lucide-react';

import { API_BASE as API_URL } from '../lib/apiBase';
export default function BirthdayNotification() {
  const { user } = useAuth();
  const [birthdays, setBirthdays] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user && !dismissed) {
      fetchBirthdays();
    }
  }, [user, dismissed]);

  const fetchBirthdays = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/birthdays/today`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.count > 0) {
          setBirthdays(data.birthdays);
          // Check if already shown today
          const lastShown = localStorage.getItem('birthday-notification-date');
          const today = new Date().toDateString();
          if (lastShown !== today) {
            setIsOpen(true);
            localStorage.setItem('birthday-notification-date', today);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch birthdays:', error);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setDismissed(true);
  };

  if (birthdays.length === 0) return null;

  return (
    <>
      {/* Floating Birthday Badge - Always visible if there are birthdays */}
      {!isOpen && birthdays.length > 0 && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-pink-500 to-purple-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform animate-bounce"
          data-testid="birthday-badge"
        >
          <div className="relative">
            <Cake className="w-6 h-6" />
            <span className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {birthdays.length}
            </span>
          </div>
        </button>
      )}

      {/* Birthday Popup Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950 dark:to-purple-950 border-2 border-pink-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <PartyPopper className="w-8 h-8 text-pink-500 animate-bounce" />
              <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                Birthday Celebration!
              </span>
              <PartyPopper className="w-8 h-8 text-purple-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Decorative Header */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full text-sm font-medium">
                <Cake className="w-4 h-4" />
                {birthdays.length === 1 ? "Today's Birthday" : `${birthdays.length} Birthdays Today!`}
              </div>
            </div>

            {/* Birthday Cards */}
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {birthdays.map((person, index) => (
                <Card 
                  key={person.employee_id} 
                  className="bg-white dark:bg-slate-800 border-2 border-pink-100 dark:border-pink-800 overflow-hidden"
                  data-testid={`birthday-card-${person.employee_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Avatar with Birthday Hat */}
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                          {person.name?.charAt(0) || '?'}
                        </div>
                        <div className="absolute -top-2 -right-1 text-2xl">🎂</div>
                      </div>
                      
                      {/* Person Details */}
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                          {person.name}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {person.designation || 'Employee'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-500">
                          {person.department}
                        </p>
                        {person.age && (
                          <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full text-xs">
                            <Gift className="w-3 h-3" />
                            Turning {person.age}!
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Wish Message */}
            <div className="text-center p-4 bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-900/50 dark:to-purple-900/50 rounded-lg">
              <p className="text-lg font-medium text-slate-700 dark:text-slate-200">
                🎉 Wishing {birthdays.length === 1 ? 'them' : 'everyone'} a wonderful birthday! 🎉
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                May this special day bring joy and happiness!
              </p>
            </div>

            {/* Close Button */}
            <div className="flex justify-center pt-2">
              <Button 
                onClick={handleClose}
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8"
                data-testid="close-birthday-popup"
              >
                <PartyPopper className="w-4 h-4 mr-2" />
                Celebrate!
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
